import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../../components/layout/AppLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'delivery_transport', label: 'Delivery & Transport' },
  { value: 'home_services', label: 'Home Services' },
  { value: 'errands_tasks', label: 'Errands & Tasks' },
  { value: 'shopping_assistance', label: 'Shopping Assistance' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'tutoring_education', label: 'Tutoring & Education' },
  { value: 'events_entertainment', label: 'Events & Entertainment' },
  { value: 'tech_support', label: 'Tech Support' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'items_goods', label: 'Items & Goods' },
  { value: 'other', label: 'Other' }
];

export const CreateWantOfferPost = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    post_type: 'want',
    title: '',
    description: '',
    category: '',
    location_country: 'South Africa',
    location_city: '',
    location_area: '',
    budget: '',
    currency: 'ZAR',
    photos: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || formData.title.length < 5) {
      toast.error('Title must be at least 5 characters');
      return;
    }
    
    if (!formData.description || formData.description.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.location_city) {
      toast.error('Please enter your city');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/wants-offers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          post_type: formData.post_type,
          title: formData.title,
          description: formData.description,
          category: formData.category,
          location_country: formData.location_country,
          location_city: formData.location_city,
          location_area: formData.location_area || null,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          currency: formData.currency,
          photos: formData.photos
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Post created successfully!');
        if (data.matches_found > 0) {
          toast.info(`Found ${data.matches_found} potential matches!`);
        }
        navigate(`/wants-offers/${data.post_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create post');
      }
    } catch (error) {
      console.error('Failed to create post:', error);
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto p-4 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wants-offers')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Post
            </h1>
            <p className="text-muted-foreground">
              Post a want (something you need) or an offer (something you can provide)
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Post Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Post Type */}
              <div>
                <Label>What are you posting?</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, post_type: 'want' })}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      formData.post_type === 'want'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-lg">I WANT</div>
                    <div className="text-sm text-muted-foreground">
                      Something you need or are looking for
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, post_type: 'offer' })}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      formData.post_type === 'offer'
                        ? 'border-green-500 bg-green-50 dark:bg-green-950'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-lg">I OFFER</div>
                    <div className="text-sm text-muted-foreground">
                      Something you can provide or sell
                    </div>
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief description of what you want or offer"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Provide more details..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  required
                />
              </div>

              {/* Category */}
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="location_city">City *</Label>
                  <Input
                    id="location_city"
                    placeholder="e.g., Johannesburg, Cape Town"
                    value={formData.location_city}
                    onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="location_area">Area (Optional)</Label>
                  <Input
                    id="location_area"
                    placeholder="e.g., Sandton, Sea Point"
                    value={formData.location_area}
                    onChange={(e) => setFormData({ ...formData, location_area: e.target.value })}
                  />
                </div>
              </div>

              {/* Budget */}
              <div>
                <Label htmlFor="budget">
                  Budget (Optional) - {formData.post_type === 'want' ? 'Maximum you can pay' : 'Asking price'}
                </Label>
                <div className="flex gap-2">
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-24 px-3 py-2 border rounded-lg"
                  >
                    <option value="ZAR">ZAR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/wants-offers')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Creating...' : 'Create Post'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2">Tips for posting:</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Be specific and clear about what you want or offer</li>
              <li>• Include relevant details like size, quantity, timeline</li>
              <li>• Set a realistic budget or price</li>
              <li>• Posts automatically expire after 30 days</li>
              <li>• You can edit or close your post anytime</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};
